import { T, useGT } from 'gt-react';
import { selectEmailBody, type EmailBodyContent } from './select-email-body';

const RENDERED_HTML_CLASS_NAME = `prose prose-sm max-w-none overflow-x-auto
  [&_img]:max-w-full
  [&_a]:text-primary [&_a]:underline
  [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-sm
  [&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-2 [&_h4]:mb-2 [&_h5]:mb-2 [&_h6]:mb-2
  [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:pl-6 [&_ol]:mb-4
  [&_blockquote]:border-l-4 [&_blockquote]:border-muted [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:mb-4 [&_blockquote]:italic
  [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4
  [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm`;

export function PlainTextEmailBody({ content }: { content: string }) {
  const gt = useGT();

  return (
    <div
      role="region"
      aria-label={gt('Email body', {
        context: 'accessible label for the scrollable plain-text email body',
      })}
      tabIndex={0}
      className="min-w-0 max-w-full overflow-x-auto focus-visible:outline-2 focus-visible:outline-ring"
    >
      {/* pre-wrap keeps newlines and column padding but soft-wraps lines wider
          than the panel, matching how mail clients render text/plain. Plain
          `pre` forced ~3k prose emails to scroll horizontally. */}
      <div className="font-mono text-sm leading-6 whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}

/**
 * The one place an email body is turned into DOM. Callers own the chrome
 * (card, sheet, modal); this owns format selection, sanitization and the
 * empty state. Any email DTO satisfies `email` structurally.
 */
export function EmailBodyView({
  email,
  cidMap,
}: {
  email: EmailBodyContent;
  cidMap?: Map<string, string>;
}) {
  // React Compiler memoizes this; selectEmailBody runs DOMPurify + a second
  // parse, so keeping the inputs (html/text/cidMap) referentially stable at
  // call sites still matters.
  const body = selectEmailBody(email, cidMap);

  switch (body.format) {
    case 'text':
      return <PlainTextEmailBody content={body.content} />;
    case 'html':
      return (
        <div
          className={RENDERED_HTML_CLASS_NAME}
          dangerouslySetInnerHTML={{
            __html: body.content,
          }}
        />
      );
    case 'empty':
      return (
        <div className="flex items-center justify-center p-3 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground italic opacity-50 tracking-wide">
            <T context="email viewer">No email body content</T>
          </p>
        </div>
      );
    default: {
      const exhaustive: never = body;
      return exhaustive;
    }
  }
}
